/**
 * PRD-任务双向关联管理
 * 负责管理 PRD 与任务之间的关联关系
 */

import { ExtendedTask } from '../types/extended-task';
import { TaskStore } from './task-store';
import { logger } from '../utils/logger';
import fs from 'fs-extra';
import path from 'path';

export interface PRDMeta {
  id: string;
  title: string;
  path: string;
  createdAt: Date;
  updatedAt: Date;
  associatedTasks: string[]; // 任务ID列表
}

export interface PRDTaskAssociation {
  prdId: string;
  taskIds: string[];
  createdAt: Date;
  updatedAt: Date;
}

export class PRDTaskAssociationManager {
  private taskStore: TaskStore;
  private workflowDir: string;
  private prdMetaPath: string;
  private associations: Map<string, PRDTaskAssociation> = new Map();
  
  constructor(workflowDir: string = process.cwd()) {
    this.workflowDir = path.join(workflowDir, '.workflow');
    this.prdMetaPath = path.join(this.workflowDir, 'prd-meta.json');
    this.taskStore = new TaskStore(workflowDir);
  }
  
  /**
   * 初始化，加载现有的关联数据
   */
  async initialize(): Promise<void> {
    try {
      if (await fs.pathExists(this.prdMetaPath)) {
        const data = await fs.readJson(this.prdMetaPath);
        if (data.associations) {
          data.associations.forEach((assoc: PRDTaskAssociation) => {
            this.associations.set(assoc.prdId, {
              ...assoc,
              createdAt: new Date(assoc.createdAt),
              updatedAt: new Date(assoc.updatedAt)
            });
          });
        }
      }
    } catch (error) {
      logger.error('Failed to load PRD associations:', error);
    }
  }
  
  /**
   * 保存关联数据
   */
  private async saveAssociations(): Promise<void> {
    const data = {
      associations: Array.from(this.associations.values()),
      updatedAt: new Date().toISOString()
    };
    
    await fs.ensureDir(path.dirname(this.prdMetaPath));
    await fs.writeJson(this.prdMetaPath, data, { spaces: 2 });
  }
  
  /**
   * 创建 PRD 与任务的关联
   */
  async associatePRDWithTasks(prdId: string, taskIds: string[]): Promise<void> {
    // 1. 更新关联映射
    const existing = this.associations.get(prdId);
    if (existing) {
      // 合并任务ID，去重
      const mergedTaskIds = Array.from(new Set([...existing.taskIds, ...taskIds]));
      existing.taskIds = mergedTaskIds;
      existing.updatedAt = new Date();
    } else {
      this.associations.set(prdId, {
        prdId,
        taskIds,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    // 2. 更新任务的 prdId 字段
    for (const taskId of taskIds) {
      const task = await this.taskStore.getTaskById(taskId);
      if (task) {
        await this.taskStore.updateTask(taskId, {
          prdId,
          metadata: {
            ...task.metadata,
            prdAssociatedAt: new Date().toISOString()
          }
        });
        logger.info(`Associated task ${taskId} with PRD ${prdId}`);
      }
    }
    
    // 3. 保存关联数据
    await this.saveAssociations();
  }
  
  /**
   * 解除 PRD 与任务的关联
   */
  async disassociatePRDFromTasks(prdId: string, taskIds: string[]): Promise<void> {
    const association = this.associations.get(prdId);
    if (!association) return;
    
    // 1. 从关联中移除任务
    association.taskIds = association.taskIds.filter(id => !taskIds.includes(id));
    association.updatedAt = new Date();
    
    if (association.taskIds.length === 0) {
      this.associations.delete(prdId);
    }
    
    // 2. 清除任务的 prdId 字段
    for (const taskId of taskIds) {
      const task = await this.taskStore.getTaskById(taskId);
      if (task && task.prdId === prdId) {
        await this.taskStore.updateTask(taskId, {
          prdId: undefined,
          metadata: {
            ...task.metadata,
            prdDisassociatedAt: new Date().toISOString()
          }
        });
        logger.info(`Disassociated task ${taskId} from PRD ${prdId}`);
      }
    }
    
    // 3. 保存关联数据
    await this.saveAssociations();
  }
  
  /**
   * 获取 PRD 关联的所有任务
   */
  async getTasksByPRD(prdId: string): Promise<ExtendedTask[]> {
    return await this.taskStore.getTasksByPrdId(prdId);
  }
  
  /**
   * 获取任务关联的 PRD
   */
  async getPRDByTask(taskId: string): Promise<string | null> {
    const task = await this.taskStore.getTaskById(taskId);
    return task?.prdId || null;
  }
  
  /**
   * 获取所有 PRD 关联信息
   */
  getAllAssociations(): PRDTaskAssociation[] {
    return Array.from(this.associations.values());
  }
  
  /**
   * 根据 PRD 内容自动创建任务
   */
  async createTasksFromPRD(prdId: string, prdContent: string, prdTitle: string): Promise<ExtendedTask[]> {
    const createdTasks: ExtendedTask[] = [];
    
    try {
      // 解析 PRD 内容，提取任务
      const taskSections = this.extractTasksFromPRD(prdContent);
      
      // 创建任务
      for (const section of taskSections) {
        const task = await this.taskStore.addTask({
          content: section.content,
          priority: section.priority || 'medium',
          tags: section.tags || ['from-prd'],
          metadata: {
            source: 'prd',
            prdTitle,
            prdSection: section.section,
            extractedAt: new Date().toISOString()
          }
        });
        
        createdTasks.push(task);
      }
      
      // 建立关联
      if (createdTasks.length > 0) {
        const taskIds = createdTasks.map(t => t.id);
        await this.associatePRDWithTasks(prdId, taskIds);
        
        logger.info(`Created ${createdTasks.length} tasks from PRD ${prdId}`);
      }
      
    } catch (error) {
      logger.error(`Failed to create tasks from PRD ${prdId}:`, error);
    }
    
    return createdTasks;
  }
  
  /**
   * 从 PRD 内容中提取任务
   */
  private extractTasksFromPRD(content: string): Array<{
    content: string;
    section: string;
    priority?: 'urgent' | 'high' | 'medium' | 'low';
    tags?: string[];
  }> {
    const tasks: Array<{
      content: string;
      section: string;
      priority?: 'urgent' | 'high' | 'medium' | 'low';
      tags?: string[];
    }> = [];
    
    // 查找任务部分的标记
    const taskPatterns = [
      /## 任务列表\s*\n([\s\S]*?)(?=\n##|$)/gi,
      /## Tasks?\s*\n([\s\S]*?)(?=\n##|$)/gi,
      /## 开发任务\s*\n([\s\S]*?)(?=\n##|$)/gi,
      /## Implementation Tasks?\s*\n([\s\S]*?)(?=\n##|$)/gi
    ];
    
    for (const pattern of taskPatterns) {
      const matches = content.matchAll(pattern);
      for (const match of matches) {
        const section = match[0].split('\n')[0].replace(/^##\s*/, '');
        const taskContent = match[1];
        
        // 提取每个任务项
        const taskLines = taskContent.split('\n').filter(line => line.trim());
        for (const line of taskLines) {
          // 匹配常见的任务格式
          const taskMatch = line.match(/^[-*]\s*(.+)/);
          if (taskMatch) {
            const taskText = taskMatch[1].trim();
            
            // 提取优先级
            let priority: 'urgent' | 'high' | 'medium' | 'low' = 'medium';
            if (taskText.includes('[紧急]') || taskText.includes('[urgent]')) {
              priority = 'urgent';
            } else if (taskText.includes('[高]') || taskText.includes('[high]')) {
              priority = 'high';
            } else if (taskText.includes('[低]') || taskText.includes('[low]')) {
              priority = 'low';
            }
            
            // 提取标签
            const tags: string[] = ['from-prd'];
            const tagMatch = taskText.match(/#(\w+)/g);
            if (tagMatch) {
              tags.push(...tagMatch.map(t => t.substring(1)));
            }
            
            // 清理任务文本
            const cleanedText = taskText
              .replace(/\[(紧急|高|中|低|urgent|high|medium|low)\]/gi, '')
              .replace(/#\w+/g, '')
              .trim();
            
            if (cleanedText) {
              tasks.push({
                content: cleanedText,
                section,
                priority,
                tags
              });
            }
          }
        }
      }
    }
    
    // 如果没有找到明确的任务部分，尝试查找功能需求
    if (tasks.length === 0) {
      const featurePatterns = [
        /## 功能需求\s*\n([\s\S]*?)(?=\n##|$)/gi,
        /## Features?\s*\n([\s\S]*?)(?=\n##|$)/gi,
        /## 需求说明\s*\n([\s\S]*?)(?=\n##|$)/gi
      ];
      
      for (const pattern of featurePatterns) {
        const matches = content.matchAll(pattern);
        for (const match of matches) {
          const section = '功能需求';
          const featureContent = match[1];
          
          const featureLines = featureContent.split('\n').filter(line => line.trim());
          for (const line of featureLines) {
            const featureMatch = line.match(/^[-*]\s*(.+)/);
            if (featureMatch) {
              const featureText = featureMatch[1].trim();
              if (featureText && !featureText.includes('：')) {
                tasks.push({
                  content: `实现${featureText}`,
                  section,
                  priority: 'medium',
                  tags: ['from-prd', 'feature']
                });
              }
            }
          }
        }
      }
    }
    
    return tasks;
  }
  
  /**
   * 同步 PRD 变更到任务
   */
  async syncPRDChangesToTasks(prdId: string, updatedPRDContent: string): Promise<void> {
    const association = this.associations.get(prdId);
    if (!association) return;
    
    // 获取关联的任务
    const tasks = await this.getTasksByPRD(prdId);
    
    // 这里可以实现更复杂的同步逻辑
    // 例如：检测 PRD 中的变更，更新相应的任务描述
    
    for (const task of tasks) {
      await this.taskStore.updateTask(task.id, {
        metadata: {
          ...task.metadata,
          prdLastSyncedAt: new Date().toISOString()
        }
      });
    }
    
    logger.info(`Synced PRD ${prdId} changes to ${tasks.length} tasks`);
  }
}